import {useCallback, useState} from 'react';
import {NextPage} from 'next';
import NextLink from 'next/link';
import {useRouter} from 'next/router';

import {Button, Screen} from '#component/base';
import {AuthCTAMessages} from '#component/partial';
import {useAuth, useOnceEffect} from '#hook';
import {useReturnUrl} from '#hook';
import {resolveRedirectUrl} from '#hook/useReturnUrl';
import {sprinkles} from '#style/sprinkles';

export const SignOut: NextPage = () => {
  const [renderContent, setRenderContent] = useState<boolean>(false);
  const {signOut} = useAuth();
  const router = useRouter();
  const {targetResolver} = useReturnUrl();

  const {return_to: returnUrl} = router.query;

  const onPageLoad = useCallback(async () => {
    const resolveReturnUrl = targetResolver(returnUrl as string);
    const redirectUrl = await resolveRedirectUrl(resolveReturnUrl, null);

    await signOut({redirect: redirectUrl});

    if (!redirectUrl) {
      setRenderContent(true);
    }
  }, [signOut, returnUrl, targetResolver]);

  useOnceEffect(() => {
    onPageLoad();
  });

  return (
    <Screen
      // Because this page also handles redirection to return URL, we should
      // not set the `userState` prop to disable user state checks. This way
      // we can avoid potential conflicts and complications.
      renderContent={renderContent}
      pageName="Sign Out"
      sidePanelImageUrl="/image/reset-password.jpg"
      contentContainerClassName={sprinkles({flex: 'flex', dir: 'col', content: 'center'})}>
      <div
        className={sprinkles({
          flex: 'flex',
          direction: 'col',
          h: 'full',
          placeContent: ['end', 'end', 'center'],
        })}>
        <AuthCTAMessages
          title={
            <>
              Successfully
              <br />
              logged out
            </>
          }
        />

        <div className={sprinkles({flex: 'flex', direction: ['col', 'col', 'row'], gap: 8})}>
          <NextLink href="/signin" passHref>
            <Button as="a">Log in</Button>
          </NextLink>

          <NextLink href="/" passHref>
            <Button as="a" variant="secondary">
              Go to homepage
            </Button>
          </NextLink>
        </div>
      </div>
    </Screen>
  );
};
